import {
  AtoneAuthenticateGetResponseMemberTypeEnum,
  AtoneAuthenticateGetResponseSexDivisionEnum,
  AtoneMemberTypePostResponseMemberTypeEnum,
  AtoneRequestTokenPostRequestSexDivisionEnum,
  AtoneTracksPostRequestAuthenticationTypeEnum,
} from '@netprotections/berlin-bff';
import { reactive, readonly, ref, unref, computed, watch, Ref } from '@vue/composition-api';
import { useQuery } from 'vue-query';
import { Route } from 'vue-router';
import { useContext } from '@berlin-front/common/composables/context';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import { useSessionId } from '@berlin-front/common/composables/useSessionId';
import useFingerprint from '@berlin-front/common/composables/useFingerprint';
import useOrAll from '@berlin-front/common/composables/useOrAll';
import { event, set } from 'vue-gtag';
import { BerlinBffContext } from '@/services/BerlinBff';
import { ApiCacheContext } from '@/services/ApiCacheContext';
import { notNil, pick, ValueTypeOf } from '@berlin-front/common/utils';
import { ROUTE_NAME } from '@/router/route-name';
import { ModalBridgeContext } from '@services/ModalBridgeContext';

/**
 * リクエストトークン取得
 *
 * @see https://www.figma.com/file/F6yaNi23oLeiMBIRbLuDlD/%E5%85%B1%E9%80%9AIF%E7%89%88-%E8%AA%8D%E8%A8%BC%E3%83%A2%E3%83%BC%E3%83%80%E3%83%AB?type=whiteboard&node-id=45-382&t=J20OCkgY9krBpq2B-4
 */
const useRequestTokenPost = () => {
  const { atoneLiteAuthModalParameter, setAtoneRequestTokenPostResult } = useContext(ApiCacheContext);
  const { requestToken, payment } = useContext(BerlinBffContext);
  const globals = useContext(ModalBridgeContext);

  // リクエストトークン発行API
  const {
    //
    isLoading,
    isSuccess,
    isError,
    data,
  } = useQuery(
    // 条件変更時のリクエスト実行は行わないため、クエリキーは定数で良い。
    'atoneLite.RequestTokenPost',
    // stateにパラメタセットして、そっから始めるのがuseQueryの作法。
    () =>
      Promise.all([
        requestToken.atoneRequestTokenControllerRequestTokenPost({
          shopPublicKey: atoneLiteAuthModalParameter.value.pubKey, // 必須パラメータ。
          mailAddress: atoneLiteAuthModalParameter.value.payment.customer?.email,
          familyName: atoneLiteAuthModalParameter.value.payment.customer?.customerFamilyName,
          familyNameKana: atoneLiteAuthModalParameter.value.payment.customer?.customerFamilyNameKana,
          givenName: atoneLiteAuthModalParameter.value.payment.customer?.customerGivenName,
          givenNameKana: atoneLiteAuthModalParameter.value.payment.customer?.customerGivenNameKana,
          // TODO: Enum調整してasとる
          sexDivision: atoneLiteAuthModalParameter.value.payment.customer
            ?.sexDivision as unknown as AtoneRequestTokenPostRequestSexDivisionEnum,
          birthday: atoneLiteAuthModalParameter.value.payment.customer?.birthday,
          zipCode: atoneLiteAuthModalParameter.value.payment.customer?.zipCode,
          address: atoneLiteAuthModalParameter.value.payment.customer?.address,
          phoneNumber: atoneLiteAuthModalParameter.value.payment.customer?.phoneNumber,
        }),
        payment.atonePaymentControllerParameterPost(
          atoneLiteAuthModalParameter.value.pubKey,
          {
            requestId: globals.requestId,
            serviceType: '04',
            modalMode: '02',
            ...pick(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              atoneLiteAuthModalParameter.value as unknown as any,
              'amount',
              'checksum',
              'customer',
              'description',
              'destCustomers',
              'items',
              'salesForm',
              'salesSettled',
              'serviceSupplier',
              'shopTransactionNo',
              'sourceMedia',
              'transactionOptions',
              'userNo',
            ),
          },
          atoneLiteAuthModalParameter.value.terminalId,
        ),
      ] as const).then((v) => v[0]),
    {
      onSuccess: ({ data: result }) => {
        setAtoneRequestTokenPostResult(result);
      },
    },
  );
  return { isLoading, isSuccess, isError, data };
};

/**
 * pretoken認証
 *
 * @see https://www.figma.com/file/F6yaNi23oLeiMBIRbLuDlD/%E5%85%B1%E9%80%9AIF%E7%89%88-%E8%AA%8D%E8%A8%BC%E3%83%A2%E3%83%BC%E3%83%80%E3%83%AB?type=whiteboard&node-id=45-386&t=ITCiH4EvztDHI11U-4
 */
const useLoginWithPretoken = () => {
  const { authenticate } = useContext(BerlinBffContext);
  const { atoneLiteAuthModalParameter, tokens, setAtoneAuthenticateGetResult, setNpTokenAndSignature } =
    useContext(ApiCacheContext);
  const globals = useContext(ModalBridgeContext);

  const { isLoading, isSuccess, isError, data } = useQuery(
    'atoneLite.authenticateGet',
    () =>
      authenticate.atoneAuthenticateControllerAuthenticateGet(
        tokens.value?.npToken ?? '',
        atoneLiteAuthModalParameter.value.pubKey,
        useSessionId(),
        globals.requestId,
      ),
    {
      enabled: computed(() => (tokens.value?.npToken ?? '').length > 0),
      onSuccess: ({ data: result }) => {
        setAtoneAuthenticateGetResult(result);
        setNpTokenAndSignature(tokens.value?.npToken, result.signature);
        window.newrelic.addPageAction('authenticate_by_token', {
          member_id: notNil(result.memberCode),
          shop_id: notNil(result.shopId),
          shop_name: notNil(result.shopName),
          gaid: notNil(result.hashId),
        });

        let gender: string | null;
        if (result.memberType === AtoneAuthenticateGetResponseMemberTypeEnum.NUMBER_3) {
          gender = null;
          // eslint-disable-next-line no-underscore-dangle
        } else if (result.sexDivision === AtoneAuthenticateGetResponseSexDivisionEnum._1) {
          gender = '男性';
          // eslint-disable-next-line no-underscore-dangle
        } else if (result.sexDivision === AtoneAuthenticateGetResponseSexDivisionEnum._2) {
          gender = '女性';
        } else {
          gender = '不明';
        }

        set({
          user_id: notNil(result.memberCode),
          user_properties: {
            member_id: notNil(result.memberCode),
            shop_id: notNil(result.shopId),
            gender,
            age: result.memberType === AtoneAuthenticateGetResponseMemberTypeEnum.NUMBER_3 ? null : result.age,
            // shop_name: notNil(result.shopName),
            guest: result.memberType === AtoneAuthenticateGetResponseMemberTypeEnum.NUMBER_3 ? 'yes' : 'no',
          },
        });

        event('login', {
          method: 'Token Lite',
        });
      },
    },
  );
  return {
    isLoading: computed(() => ((tokens.value?.npToken ?? '').length > 0 ? isLoading.value : false)),
    isSuccess: computed(() => ((tokens.value?.npToken ?? '').length > 0 ? isSuccess.value : false)),
    isError: computed(() => ((tokens.value?.npToken ?? '').length > 0 ? isError.value : false)),
    data: computed(() => ((tokens.value?.npToken ?? '').length > 0 ? data.value : undefined)),
  };
};

/**
 * fingerprint check
 * チェックがダメだった場合は、sms送信までをここで行う。
 * @param param
 * @returns
 */
const useFingerprintCheck = (param: { enabled: Ref<boolean> }) => {
  const fingerprint = useFingerprint();
  const {
    tokens,
    atoneLiteAuthModalParameter,
    setAtoneTracksPostResult,
    setAtoneTracksTrustResult,
    setAtoneSmsNotifyPostResult,
  } = useContext(ApiCacheContext);
  const { tracks, sms } = useContext(BerlinBffContext);

  const {
    isLoading: isTrackTokenPostLoading,
    isSuccess: isTrackTokenPostSucceeded,
    isError: isTrackTokenPostError,
    data: trackTokenPostResult,
  } = useQuery(
    'atoneLite.init.trackTokenPost',
    () =>
      tracks.atoneTracksControllerTrackTokenPost(
        notNil(tokens.value.npToken),
        notNil(atoneLiteAuthModalParameter.value.pubKey),
        {
          authenticationType: AtoneTracksPostRequestAuthenticationTypeEnum.NUMBER_1,
          fingerPrint: fingerprint.value,
        },
      ),
    {
      enabled: computed(() => fingerprint.resolved && param.enabled.value),
      onSuccess({ data }) {
        setAtoneTracksPostResult(data);
      },
    },
  );

  const {
    isLoading: isTrackTokenTrustLoading,
    isSuccess: isTrackTokenTrustSucceeded,
    isError: isTrackTokenTrustError,
    data: trackTokenTrustResult,
  } = useQuery(
    'atoneLite.init.TrackTokenTrust',
    () =>
      tracks.atoneTracksControllerTrackTokenTrust(
        notNil(trackTokenPostResult.value?.data.trackToken),
        notNil(tokens.value.npToken),
        notNil(atoneLiteAuthModalParameter.value.pubKey),
      ),
    {
      enabled: isTrackTokenPostSucceeded,
      onSuccess({ data }) {
        setAtoneTracksTrustResult(data);
      },
    },
  );
  const isSuccess = computed(
    () => isTrackTokenTrustSucceeded.value && (trackTokenTrustResult.value?.data.trustedFlag ?? false),
  );

  // trackTokenがtrustされてない時はそのまま次のsms認証のページのためにsmsNotifyを事前呼び出しする。
  const {
    isLoading: isSmsNotifyPostLoading,
    isSuccess: isSmsNotifyPostSucceeded,
    isError: isSmsNotifyPostError,
  } = useQuery(
    'atoneLite.init.SmsNotifyPost',
    () =>
      sms.atoneSmsControllerSmsNotifyPost(
        notNil(tokens.value.npToken),
        notNil(atoneLiteAuthModalParameter.value.pubKey),
        {
          trackToken: notNil(trackTokenPostResult.value?.data.trackToken),
          resend: false,
        },
      ),
    {
      // trustedFlagはundefinedのケースもある。明示的にfalseであることが必要なため、等号比較する必要がある。
      enabled: computed(() => trackTokenTrustResult.value?.data.trustedFlag === false),
      onSuccess({ data }) {
        setAtoneSmsNotifyPostResult(data);
      },
    },
  );
  // トラックトークンが認証済みでない場合、smsNotifyを送信するが、その結果が成否かかわらず実行が完了した段階でfailとする。
  // このfailを検出すると、sms認証画面へと遷移する。
  const isFail = useOrAll(isSmsNotifyPostSucceeded, isSmsNotifyPostError);

  return {
    isLoading: useOrAll(isTrackTokenPostLoading, isTrackTokenTrustLoading, isSmsNotifyPostLoading),
    isSuccess,
    isFail,
    isError: useOrAll(isTrackTokenTrustError, isTrackTokenPostError),
  };
};

/**
 * 会員タイプの判定
 *
 * @see https://www.figma.com/file/F6yaNi23oLeiMBIRbLuDlD/%E5%85%B1%E9%80%9AIF%E7%89%88-%E8%AA%8D%E8%A8%BC%E3%83%A2%E3%83%BC%E3%83%80%E3%83%AB?type=whiteboard&node-id=45-399&t=ITCiH4EvztDHI11U-4
 * @param param
 */
const useMemberTypePost = (param: { enabled: Ref<boolean> }) => {
  const { member } = useContext(BerlinBffContext);
  const { tokens, atoneLiteAuthModalParameter, setAtoneMemberTypePostResult } = useContext(ApiCacheContext);
  const { isLoading, isSuccess, isError, data } = useQuery(
    'atoneLite.MemberTypePost',
    async () => {
      if (atoneLiteAuthModalParameter.value.payment.customer?.email) {
        return member.atoneMemberControllerMemberTypePost(notNil(tokens.value.requestToken), {
          mailAddress: atoneLiteAuthModalParameter.value.payment.customer?.email ?? '',
        });
      }
      // payment.customer.emailが設定されてない時は無条件で新規会員になるため、APIを呼ばない。
      // API呼んだ体で新規会員レスポンスを返す。
      return Promise.resolve({
        data: {
          memberType: AtoneMemberTypePostResponseMemberTypeEnum.NUMBER_0,
        },
      });
    },
    {
      enabled: computed(
        () =>
          param.enabled.value &&
          // (tokens.value?.npToken ?? '').length > 0 &&
          (tokens.value?.requestToken ?? '').length > 0,
      ),

      onSuccess: ({ data: result }) => {
        setAtoneMemberTypePostResult(result);
      },
    },
  );
  return {
    isLoading,
    isSuccess,
    isError,
    data,
  };
};

export const useLoading = (_$route: Route) => ({
  useLoading: () => {
    const router = useRouter();
    const { atoneAuthenticateGetResult, atoneMemberTypePostResult } = useContext(ApiCacheContext);
    const nextPage = ref<
      undefined | { name: ValueTypeOf<typeof ROUTE_NAME>; query?: Record<string, string | string[]> }
    >();

    // リクエストトークンを得る。
    useRequestTokenPost();
    // - preTokenでログイン
    const { isSuccess: isLoginSucceeded, isError: isLoginFailed } = useLoginWithPretoken();
    //   - フィンガープリントチェック。
    const { isSuccess: isFingerprintCheckSucceeded, isFail: isFingerprintCheckFailed } = useFingerprintCheck({
      enabled: isLoginSucceeded,
    });

    // - ログイン成功 -> フィンガープリントOK -> 認証完了
    watch([isFingerprintCheckSucceeded], () => {
      nextPage.value = { name: ROUTE_NAME.ATONE_LITE_AUTH_COMPLETE };
    });

    // - ログイン成功 かつ フィンガープリントNG -> SMS認証
    watch([isLoginSucceeded, isFingerprintCheckFailed], () => {
      if (isFingerprintCheckFailed.value && isLoginSucceeded.value && atoneAuthenticateGetResult.value) {
        nextPage.value = {
          name: ROUTE_NAME.ATONE_LITE_AUTH_ATONE_MEMBER_SMS_AUTH,
        };
      }
    });

    // - ログインNG || preTokenなし
    const { tokens } = useContext(ApiCacheContext);
    const preTokenEmpty = computed(() => (tokens.value.npToken ?? '').length === 0);
    const memberTypePostExec = computed(() => isLoginFailed.value || preTokenEmpty.value);
    //   - メンバー種別判定
    useMemberTypePost({ enabled: memberTypePostExec });
    //     - 会員 -> ログイン画面
    //     - NP会員 -> NP会員ログイン画面
    //     - その他 -> 仮会員ログイン画面
    watch([atoneMemberTypePostResult], () => {
      if (atoneMemberTypePostResult.value && 'memberType' in atoneMemberTypePostResult.value) {
        switch (notNil(atoneMemberTypePostResult.value.memberType)) {
          // NP会員。
          case AtoneMemberTypePostResponseMemberTypeEnum.NUMBER_1: {
            nextPage.value = {
              name: ROUTE_NAME.ATONE_LITE_AUTH_NP_MEMBER,
            };
            break;
          }
          // 会員
          case AtoneMemberTypePostResponseMemberTypeEnum.NUMBER_2: {
            nextPage.value = {
              name: ROUTE_NAME.ATONE_LITE_AUTH_ATONE_MEMBER_LOGIN,
            };
            break;
          }
          // 新規
          // 仮会員
          case AtoneMemberTypePostResponseMemberTypeEnum.NUMBER_0:
          case AtoneMemberTypePostResponseMemberTypeEnum.NUMBER_3: {
            nextPage.value = {
              name: ROUTE_NAME.ATONE_LITE_AUTH_GUEST_LOGIN,
            };
            break;
          }
          default: {
            // 例外。
          }
        }
      }
    });

    // 進捗バー用。
    const isCompleted = computed(() => nextPage.value !== undefined);
    const isLoading = computed(() => !unref(isCompleted));

    // ローディング終了時の処理
    const onComplete = () => {
      const location = unref(nextPage);
      if (location) {
        void router.replace(location);
      }
    };

    return readonly(
      reactive({
        title: '',
        duration: 5000,
        onComplete,
        isLoading,
        isCompleted,
      }),
    );
  },
});
